const GENERIC_OFFER_NAMES = new Set([
    'open',
    'standard',
    'standard admission',
    'general admission',
    'ga',
]);
const SESSION_OFFERS = [
    {
        match: /\ball\s*day\b|\bday\s*pass\b/i,
        label: 'ALL DAY PASS',
        shortLabel: 'ALL DAY',
        window: 'ALL DAY',
        bareNames: ['day pass', 'all day', 'all day pass'],
    },
    {
        match: /\bprelims?\b/i,
        label: 'PRELIMS',
        shortLabel: 'PRELIMS',
        window: 'MORNING UNTIL 4:00',
        bareNames: ['prelim', 'prelims'],
    },
    {
        match: /\bfinals?\b/i,
        label: 'FINALS',
        shortLabel: 'FINALS',
        window: '6:00PM UNTIL END',
        bareNames: ['final', 'finals'],
    },
];
function looksLikeSessionOffer(name) {
    return SESSION_OFFERS.some((session) => session.match.test(name));
}
function offerName(ticket) {
    const fromOffer = String(ticket?.offer?.name || ticket?.offerName || '').trim();
    if (fromOffer)
        return fromOffer;
    // Sold tickets store the offer name on ticket.name; offer_uuid is often empty.
    const fromTicket = String(ticket?.name || '').trim();
    if (fromTicket && looksLikeSessionOffer(fromTicket))
        return fromTicket;
    return '';
}
function shortDescription(ticket) {
    const description = String(ticket?.offer?.description || '').trim();
    if (!description || description.includes('\n') || description.length > 40) {
        return '';
    }
    return description;
}
function sessionLabel(name, session) {
    if (session.bareNames.includes(name.toLowerCase()))
        return session.label;
    return name.toUpperCase();
}
export function resolvePrintedOfferInfo(ticket) {
    const name = offerName(ticket);
    if (!name)
        return null;
    if (GENERIC_OFFER_NAMES.has(name.toLowerCase()))
        return null;
    for (const session of SESSION_OFFERS) {
        if (session.match.test(name)) {
            return {
                label: sessionLabel(name, session),
                shortLabel: session.shortLabel,
                window: session.window,
            };
        }
    }
    return {
        label: name,
        shortLabel: name,
        window: shortDescription(ticket),
    };
}
export function formatPrintedOfferLine(ticket) {
    const info = resolvePrintedOfferInfo(ticket);
    if (!info)
        return '';
    if (!info.window || /\d/.test(info.label))
        return info.label;
    return `${info.label} • ${info.window}`;
}
function toSelectedOffer(offer) {
    if (!offer?.name && !offer?.uuid && !offer?.id)
        return null;
    return {
        id: offer.id ?? null,
        uuid: offer.uuid || null,
        name: offer.name || null,
        description: offer.description || null,
    };
}
function seatLocationKey(value) {
    const section = String(value?.sectionNumber ?? value?.section_number ?? '').trim();
    const row = String(value?.rowNumber ?? value?.row_number ?? '').trim();
    const seat = String(value?.seatNumber ?? value?.seat_number ?? '').trim();
    if (!section && !row && !seat)
        return '';
    return `${section}|${row}|${seat}`;
}
function selectedQuantity(selected) {
    const quantity = Number(selected?.quantity);
    if (Number.isFinite(quantity) && quantity > 0)
        return quantity;
    const allSeatIds = selected?.allSeatIds;
    if (Array.isArray(allSeatIds) && allSeatIds.length) {
        return allSeatIds.length;
    }
    return 1;
}
/**
 * Box-office carts keep the sold offer on selectedFromMap, not the ticket row.
 * Stamp that offer onto reserved / reprinted tickets so thermal and PDF
 * layouts can show Day Pass / Prelims / Finals / buyer-type names.
 */
export function attachOffersFromSelectedMap(tickets, selectedFromMap) {
    if (!Array.isArray(tickets) || tickets.length === 0 || !selectedFromMap) {
        return tickets || [];
    }
    const offerBySeatId = new Map();
    const offerBySeatKey = new Map();
    const offerQueueBySection = new Map();
    const addSelected = (selected) => {
        if (!selected)
            return;
        const offer = toSelectedOffer(selected.offer);
        if (!offer)
            return;
        if (selected.seatId && !selected.GA) {
            offerBySeatId.set(String(selected.seatId), offer);
            const key = seatLocationKey(selected);
            if (key)
                offerBySeatKey.set(key, offer);
            return;
        }
        const sectionId = selected?.sectionId ?? selected?.section_id;
        if (!sectionId)
            return;
        const queue = offerQueueBySection.get(String(sectionId)) || [];
        const quantity = selectedQuantity(selected);
        for (let index = 0; index < quantity; index += 1) {
            queue.push(offer);
        }
        offerQueueBySection.set(String(sectionId), queue);
    };
    for (const item of Object.values(selectedFromMap)) {
        if (Array.isArray(item))
            item.forEach(addSelected);
        else
            addSelected(item);
    }
    if (offerBySeatId.size === 0 &&
        offerBySeatKey.size === 0 &&
        offerQueueBySection.size === 0) {
        return tickets;
    }
    return tickets.map((ticket) => {
        const fromSeatId = offerBySeatId.get(String(ticket.seatId ?? '')) ||
            offerBySeatId.get(String(ticket.seat_id ?? ''));
        const fromSeatKey = offerBySeatKey.get(seatLocationKey(ticket));
        const sectionKey = String(ticket.sectionId ?? ticket.section_id ?? '');
        const queue = offerQueueBySection.get(sectionKey);
        const fromSection = !fromSeatId && !fromSeatKey && queue?.length ? queue.shift() : null;
        const offer = fromSeatId || fromSeatKey || fromSection || ticket.offer || null;
        if (!offer)
            return ticket;
        return {
            ...ticket,
            offer,
            offerUUID: offer.uuid || ticket.offerUUID || ticket.offer_uuid || null,
        };
    });
}
